/**
 * Night sky, stars and the city on the horizon.
 *
 * ONE COMMITTED LOOK: a clear night. The arena is floodlit, so anything the sky
 * does has to lose to the floodlights — a dusk gradient competes with them and
 * the pitch stops being the brightest thing on screen. A dark sky with a warm
 * sodium glow sitting on the horizon does the opposite: it pushes the eye down
 * into the bowl and gives the far stands something to silhouette against.
 *
 * THE SKY IS TWO OBJECTS, and it has to be. The gradient is an equirect byte
 * texture on `scene.background` — no draw call, no far-plane problem, never
 * fogged. The stars are a separate `Points` cloud, because three does not
 * sample an equirect background directly: it prefilters it through PMREM, and
 * PMREM is a blur, so a star written into that texture is mathematically
 * guaranteed to be averaged out of existence. See `buildStars`.
 *
 * Nothing here touches WebGL. `skyEquirectData`, `starField` and
 * `skylineBuildings` are pure and seeded; only `makeSkyTexture`, `buildStars`
 * and `buildSkyline` allocate GPU objects, and none is called at module scope.
 */

import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  DataTexture,
  EquirectangularReflectionMapping,
  InstancedMesh,
  LinearFilter,
  LinearMipmapLinearFilter,
  Matrix4,
  MeshBasicMaterial,
  Points,
  PointsMaterial,
  RGBAFormat,
  RepeatWrapping,
  SRGBColorSpace,
  Vector3,
} from 'three';
import { StadiumRng, clamp01, rampAt } from './random';

// ---------------------------------------------------------------------------
// Gradient
// ---------------------------------------------------------------------------

/**
 * Sky colour by polar angle, in DISPLAY (sRGB) space 0..1, so the numbers below
 * are the bytes that end up in the texture and the texture is tagged
 * `SRGBColorSpace`. Position is `theta / PI`: 0 = zenith, 0.5 = horizon,
 * 1 = nadir.
 *
 * The two stops either side of 0.5 are the whole trick — a hard, narrow warm
 * band exactly on the horizon line reads as a city under the sky rather than as
 * a gradient that happens to get lighter at the bottom.
 */
export const SKY_STOPS: readonly (readonly number[])[] = [
  [0.0, 0.048, 0.066, 0.118],
  [0.28, 0.058, 0.082, 0.142],
  [0.44, 0.076, 0.104, 0.166],
  [0.488, 0.104, 0.124, 0.178],
  [0.5, 0.140, 0.130, 0.128],
  [0.512, 0.052, 0.050, 0.054],
  [0.6, 0.018, 0.020, 0.027],
  [1.0, 0.008, 0.009, 0.013],
];

/** Sky colour at `t = theta / PI`, sRGB 0..1. Pure. */
export const skyGradient = (t: number): [number, number, number] => {
  const c = rampAt(SKY_STOPS, clamp01(t));
  return [c[0], c[1], c[2]];
};

/**
 * Normal of the plane the faint galactic band lies in. Tilted off vertical so
 * the band crosses the sky diagonally instead of sitting on the horizon, where
 * it would be indistinguishable from the city glow.
 */
const BAND_NORMAL: readonly [number, number, number] = (() => {
  const n: [number, number, number] = [0.42, 0.78, -0.46];
  const l = Math.hypot(n[0], n[1], n[2]);
  return [n[0] / l, n[1] / l, n[2] / l];
})();

// ---------------------------------------------------------------------------
// Stars
// ---------------------------------------------------------------------------

export interface Star {
  /** Unit direction, THREE frame. */
  x: number;
  y: number;
  z: number;
  /** 0..1 brightness. Heavily biased faint — a uniform field looks like noise. */
  magnitude: number;
  /** 0 = cool blue-white, 1 = warm. */
  warmth: number;
}

/**
 * A seeded star field, uniform on the upper hemisphere.
 *
 * Only the upper hemisphere: everything below the horizon is behind the ground
 * plane and the stands, so half the samples would be invisible and the visible
 * half would be half as dense as asked for.
 */
export const starField = (seed: number, count: number): Star[] => {
  const rng = new StadiumRng(seed);
  const out: Star[] = [];
  for (let i = 0; i < count; i++) {
    // y = 1 - u gives uniform area density over the hemisphere.
    const y = 1 - rng.next();
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const phi = rng.next() * Math.PI * 2;
    // Squared so the field is mostly faint with a handful of bright ones.
    // Cubed (the first pass) put every star below the level where ACES at a
    // 0.98 exposure leaves anything on screen, and the sky read as empty.
    const u = rng.next();
    out.push({
      x: r * Math.cos(phi),
      y,
      z: r * Math.sin(phi),
      magnitude: u * u,
      warmth: rng.next(),
    });
  }
  return out;
};

// ---------------------------------------------------------------------------
// Equirect image
// ---------------------------------------------------------------------------

export interface SkyOptions {
  seed?: number;
  starCount?: number;
  /** Extra glow lobes on the horizon, as [azimuth fraction, strength]. */
  cityGlow?: readonly (readonly [number, number])[];
}

export const SKY_DEFAULTS = {
  seed: 0x5ea51de,
  starCount: 5200,
  /** Two lobes so the horizon is not radially symmetric. */
  cityGlow: [
    [0.18, 0.52],
    [0.62, 0.34],
  ] as const,
} as const;

/**
 * RGBA bytes for the equirect night sky, row 0 = zenith.
 *
 * Pure: given a seed it produces the same image every time, which is what makes
 * "the horizon is brighter than the zenith" and "the stars did not move" into
 * assertions rather than screenshots.
 */
export const skyEquirectData = (
  width = 1024,
  height = 512,
  options: SkyOptions = {}
): Uint8Array => {
  const starCount = options.starCount ?? SKY_DEFAULTS.starCount;
  const seed = options.seed ?? SKY_DEFAULTS.seed;
  const glows = options.cityGlow ?? SKY_DEFAULTS.cityGlow;
  const data = new Uint8Array(width * height * 4);

  for (let y = 0; y < height; y++) {
    const v = (y + 0.5) / height;
    const [gr, gg, gb] = skyGradient(v);
    const theta = v * Math.PI;
    const st = Math.sin(theta);
    const ct = Math.cos(theta);
    // Above the horizon only, fading out as it approaches it.
    const bandFade = clamp01((0.5 - v) * 6);

    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width;
      const phi = u * Math.PI * 2;
      const dx = st * Math.cos(phi);
      const dz = st * Math.sin(phi);
      const dy = ct;

      // Galactic band: a soft ridge around a great circle.
      const d = Math.abs(dx * BAND_NORMAL[0] + dy * BAND_NORMAL[1] + dz * BAND_NORMAL[2]);
      const band = Math.exp(-(d * d) / 0.012) * 0.085 * bandFade;

      // City glow lobes, hugging the horizon.
      let glow = 0;
      for (const [at, strength] of glows) {
        let a = Math.abs(u - at);
        if (a > 0.5) a = 1 - a;
        const lat = v - 0.5;
        glow += strength * Math.exp(-(a * a) / 0.010 - (lat * lat) / 0.00022);
      }

      const o = (y * width + x) * 4;
      data[o] = Math.min(255, Math.round((gr + band * 0.8 + glow * 0.30) * 255));
      data[o + 1] = Math.min(255, Math.round((gg + band * 0.9 + glow * 0.22) * 255));
      data[o + 2] = Math.min(255, Math.round((gb + band * 1.0 + glow * 0.14) * 255));
      data[o + 3] = 255;
    }
  }

  // Stars are splatted afterwards so they sit on top of the gradient rather
  // than being averaged into it.
  for (const star of starField(seed, starCount)) {
    if (star.y <= 0.02) continue;
    const uu = Math.atan2(star.z, star.x) / (Math.PI * 2) + 0.5;
    const vv = Math.acos(Math.max(-1, Math.min(1, star.y))) / Math.PI;
    // Stars near the horizon are eaten by the city glow.
    const fade = clamp01((0.48 - vv) * 8);
    if (fade <= 0) continue;
    const px = Math.round(uu * width);
    const py = Math.min(height - 1, Math.max(0, Math.round(vv * height)));
    const level = (0.30 + star.magnitude * 0.70) * fade;
    const r = Math.round(255 * level * (0.82 + star.warmth * 0.18));
    const g = Math.round(255 * level * 0.95);
    const b = Math.round(255 * level * (1 - star.warmth * 0.15));
    // The bright ones get a 2x2 core so they survive the minification a sky
    // background is drawn at; the faint ones stay one texel and twinkle.
    const wide = star.magnitude > 0.35;
    for (let dy = 0; dy <= (wide ? 1 : 0); dy++) {
      const y2 = py + dy;
      if (y2 >= height) continue;
      for (let dx = 0; dx <= (wide ? 1 : 0); dx++) {
        // Wrap in azimuth: the equirect seam is a real edge in u, not a clamp.
        const x2 = (((px + dx) % width) + width) % width;
        const o = (y2 * width + x2) * 4;
        data[o] = Math.max(data[o], r);
        data[o + 1] = Math.max(data[o + 1], g);
        data[o + 2] = Math.max(data[o + 2], b);
      }
    }
  }

  return data;
};

/** Upload the sky. Browser only. */
export const makeSkyTexture = (
  width = 2048,
  height = 1024,
  options: SkyOptions = {}
): DataTexture => {
  const texture = new DataTexture(
    skyEquirectData(width, height, options),
    width,
    height,
    RGBAFormat
  );
  texture.mapping = EquirectangularReflectionMapping;
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = RepeatWrapping;
  // NO MIPMAPS. A mip chain averages a one-texel star into its neighbours and
  // the sky comes out empty; the background is drawn at a near-constant
  // angular rate, so there is nothing for mipmapping to fix here anyway.
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
};

// ---------------------------------------------------------------------------
// Star field (geometry, not texture)
// ---------------------------------------------------------------------------

/**
 * Radius of the star sphere. Beyond every piece of stadium and city geometry so
 * the depth test occludes stars behind the roof, and well inside the camera's
 * 30,000 uu far plane — which it stays inside because the whole object is
 * re-centred on the camera every frame.
 */
export const STAR_RADIUS = 24000;

export interface StarFieldVisual {
  points: Points;
  /** Re-centre on the camera. Stars have no parallax; this is what sells that. */
  follow(x: number, y: number, z: number): void;
  dispose(): void;
}

/**
 * The stars, as a `Points` cloud rather than as texels in the sky image.
 *
 * This is not a stylistic choice, it is forced. `scene.background` with an
 * equirectangular texture does NOT sample that texture directly: three runs it
 * through `WebGLCubeUVMaps`, i.e. through PMREM, and hands the shader a
 * prefiltered cube. PMREM is a blur — it is meant to be — so every one-texel
 * star in a 2048x1024 sky is averaged into its neighbours and the sky comes out
 * as a clean empty gradient. Two full passes of raising the star brightness in
 * the texture went nowhere for exactly this reason.
 *
 * Points, drawn with `sizeAttenuation` off, give a hard 2-3 pixel dot that no
 * amount of prefiltering can touch, for one draw call.
 */
export const buildStars = (
  seed: number = SKY_DEFAULTS.seed,
  count = 2400
): StarFieldVisual => {
  const stars = starField(seed, count);
  const positions = new Float32Array(stars.length * 3);
  const colors = new Float32Array(stars.length * 3);

  for (let i = 0; i < stars.length; i++) {
    const s = stars[i];
    positions[i * 3] = s.x * STAR_RADIUS;
    positions[i * 3 + 1] = s.y * STAR_RADIUS;
    positions[i * 3 + 2] = s.z * STAR_RADIUS;
    // LINEAR intensity, and deliberately non-linear in magnitude: ACES has a
    // long toe, so a linear ramp puts nine tenths of the field below the level
    // where anything survives tone mapping.
    const level = 0.07 + s.magnitude * s.magnitude * 1.05;
    colors[i * 3] = level * (0.86 + s.warmth * 0.14);
    colors[i * 3 + 1] = level * 0.95;
    colors[i * 3 + 2] = level * (1 - s.warmth * 0.12);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('color', new BufferAttribute(colors, 3));
  geometry.computeBoundingSphere();

  const material = new PointsMaterial({
    size: 2.6,
    sizeAttenuation: false,
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    fog: false,
  });

  const points = new Points(geometry, material);
  points.name = 'stars';
  points.frustumCulled = false;
  // Behind everything. Depth TESTING stays on so the roof still occludes them.
  points.renderOrder = -1;

  return {
    points,
    follow(x: number, y: number, z: number): void {
      points.position.set(x, y, z);
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
    },
  };
};

// ---------------------------------------------------------------------------
// Skyline
// ---------------------------------------------------------------------------

export interface SkylineBuilding {
  /** Centre, THREE frame; y is implied 0 (they stand on the ground plane). */
  x: number;
  z: number;
  width: number;
  depth: number;
  height: number;
  /** Yaw so the box faces the arena, radians. */
  yaw: number;
  /** 0..1, how many of its windows are lit. */
  lit: number;
}

export const SKYLINE = {
  /** Ring the city sits in. Inside is the stadium; outside is the far plane
   *  (the camera's is 30000 uu — see src/render/camera.ts). */
  minRadius: 13000,
  maxRadius: 23000,
  minHeight: 500,
  maxHeight: 4400,
  minWidth: 480,
  maxWidth: 1700,
  count: 240,
  seed: 0xc17b5ade,
} as const;

/**
 * A seeded ring of tower blocks. Pure — the tests assert the ring bounds and
 * the determinism, which between them are the only two ways this can be wrong
 * in a way that matters (a building inside the stadium, or a skyline that
 * reshuffles every reload).
 */
export const skylineBuildings = (
  seed: number = SKYLINE.seed,
  count: number = SKYLINE.count
): SkylineBuilding[] => {
  const rng = new StadiumRng(seed);
  const out: SkylineBuilding[] = [];
  for (let i = 0; i < count; i++) {
    // Stratified in azimuth so the ring has no bald patches, jittered so it
    // has no visible period either.
    const angle = ((i + rng.next()) / count) * Math.PI * 2;
    // sqrt keeps the density even in area rather than piling up on the inside.
    const radius =
      SKYLINE.minRadius +
      (SKYLINE.maxRadius - SKYLINE.minRadius) * Math.sqrt(rng.next());
    // Nearer towers are shorter, so the far ones are not hidden.
    const depthT = clamp01(
      (radius - SKYLINE.minRadius) / (SKYLINE.maxRadius - SKYLINE.minRadius)
    );
    const tall = rng.next();
    const height =
      SKYLINE.minHeight +
      (SKYLINE.maxHeight - SKYLINE.minHeight) * tall * tall * (0.45 + 0.55 * depthT);
    out.push({
      x: Math.cos(angle) * radius,
      z: Math.sin(angle) * radius,
      width: rng.range(SKYLINE.minWidth, SKYLINE.maxWidth),
      depth: rng.range(SKYLINE.minWidth, SKYLINE.maxWidth),
      height,
      // atan2(x, z) is the yaw that turns a +Z-facing box toward the origin.
      yaw: Math.atan2(-Math.cos(angle), -Math.sin(angle)),
      lit: rng.next(),
    });
  }
  return out;
};

/**
 * Window texture for the towers: a sparse grid of lit cells on near-black.
 * `MeshBasicMaterial` so the city is unlit and reads as a silhouette; the only
 * light on it is its own windows.
 */
export const windowGridData = (
  width = 64,
  height = 128,
  seed = 0x1e2d3c4b,
  litFraction = 0.22
): Uint8Array => {
  const rng = new StadiumRng(seed);
  const data = new Uint8Array(width * height * 4);
  const cols = 8;
  const rows = 24;
  const cells: number[] = [];
  for (let i = 0; i < cols * rows; i++) {
    cells.push(rng.chance(litFraction) ? 0.35 + rng.next() * 0.65 : 0);
  }
  for (let y = 0; y < height; y++) {
    const cy = Math.floor((y / height) * rows);
    // Leave a dark gutter between floors so the windows read as windows.
    const fy = (y / height) * rows - cy;
    for (let x = 0; x < width; x++) {
      const cx = Math.floor((x / width) * cols);
      const fx = (x / width) * cols - cx;
      const inside = fx > 0.22 && fx < 0.78 && fy > 0.28 && fy < 0.8;
      const level = inside ? cells[cy * cols + cx] : 0;
      const warm = ((cx * 7 + cy * 13) % 5) / 4;
      const o = (y * width + x) * 4;
      data[o] = Math.round(14 + level * 241 * (0.72 + warm * 0.28));
      data[o + 1] = Math.round(16 + level * 225 * (0.78 + warm * 0.14));
      data[o + 2] = Math.round(22 + level * 200 * (1 - warm * 0.25));
      data[o + 3] = 255;
    }
  }
  return data;
};

export interface SkylineVisual {
  mesh: InstancedMesh;
  dispose(): void;
}

/**
 * The city, as ONE `InstancedMesh`. 240 towers is 240 boxes; as separate meshes
 * that is 240 draw calls for something the player will never look at directly.
 */
export const buildSkyline = (
  seed: number = SKYLINE.seed,
  count: number = SKYLINE.count
): SkylineVisual => {
  const buildings = skylineBuildings(seed, count);

  // Unit box with its base on y = 0, so an instance scale IS the building size.
  const geometry = new BoxGeometry(1, 1, 1);
  geometry.translate(0, 0.5, 0);

  const texture = new DataTexture(windowGridData(), 64, 128, RGBAFormat);
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.magFilter = LinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;

  const material = new MeshBasicMaterial({ map: texture, color: 0xffffff, fog: true });
  const mesh = new InstancedMesh(geometry, material, buildings.length);
  mesh.name = 'skyline';
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // It never moves and it is enormous; a per-frame frustum test on a bounding
  // sphere that contains the camera is wasted work.
  mesh.frustumCulled = false;

  const matrix = new Matrix4();
  const position = new Vector3();
  const scale = new Vector3();
  const colour = new Color();

  for (let i = 0; i < buildings.length; i++) {
    const b = buildings[i];
    position.set(b.x, 0, b.z);
    scale.set(b.width, b.height, b.depth);
    matrix.makeRotationY(b.yaw);
    matrix.scale(scale);
    matrix.setPosition(position);
    mesh.setMatrixAt(i, matrix);
    // Dim, cool, and dimmer the further out — that plus fog is the depth cue.
    const dim = 0.22 + b.lit * 0.5;
    colour.setRGB(dim * 0.62, dim * 0.72, dim);
    mesh.setColorAt(i, colour);
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

  return {
    mesh,
    dispose(): void {
      geometry.dispose();
      material.dispose();
      texture.dispose();
      mesh.dispose();
    },
  };
};
